import { useState, useEffect, useContext, useMemo } from "react";
import { fetchAllAppointments } from "../services/AppointmentService.js";
import { ThemeContext } from "../ThemeContext";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";

export default function Appointment() {
    const [appointments, setAppointments] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState([]);
    const theme = useContext(ThemeContext);

    useEffect(() => {
        loadAppointments();
    }, []);

    async function loadAppointments() {
        const data = await fetchAllAppointments();
        setAppointments(data);
    }

    const buildStatus = (status) => {
        const lower = status.toLowerCase();
        switch (lower) {
            case "pending":
                return (
                    <span
                        style={{
                            padding: "4px 8px",
                            borderRadius: 3,
                            backgroundColor: "#DFF5FA",
                            color: "#009BBF",
                            fontWeight: "bold",
                        }}
                    >
                        Pending
                    </span>
                );
            case "in_progress":
                return (
                    <span
                        style={{
                            padding: "4px 8px",
                            borderRadius: 3,
                            backgroundColor: "#ECF0F1",
                            color: "#666666",
                            fontWeight: "bold",
                        }}
                    >
                        In Progress
                    </span>
                );
            case "completed":
                return (
                    <span
                        style={{
                            padding: "4px 8px",
                            borderRadius: 3,
                            backgroundColor: "#ECF0F1",
                            color: "#666666",
                            fontWeight: "bold",
                        }}
                    >
                        Completed
                    </span>
                );
            case "cancelled":
                return (
                    <span
                        style={{
                            padding: "4px 8px",
                            borderRadius: 3,
                            backgroundColor: "#ECF0F1",
                            color: "#666666",
                            fontWeight: "bold",
                        }}
                    >
                        Cancelled
                    </span>
                );
            default:
                return (
                    <span
                        style={{
                            padding: "4px 8px",
                            borderRadius: 3,
                            backgroundColor: "#ECF0F1",
                            color: "#666666",
                            fontWeight: "bold",
                        }}
                    >
                        Unknown
                    </span>
                );
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: "ID",
                cell: (info) => info.getValue(),
            },
            {
                accessorKey: "type",
                header: "Type",
                cell: (info) => info.getValue(),
            },
            {
                accessorKey: "questionnaire_response.questionnaire.title",
                header: "Service",
                cell: (info) => info.getValue(),
            },
            {
                accessorKey: "supabase_user.name",
                header: "User",
                cell: (info) => info.getValue(),
            },
            {
                accessorKey: "appointement_status",
                header: "Status",
                cell: (info) => buildStatus(info.getValue()),
            },

            {
                accessorKey: "createdAt",
                header: "Created",
                cell: (info) =>
                    new Date(info.getValue()).toLocaleString(),
            },
        ],
        []
    );

    const table = useReactTable({
        data: appointments,
        columns,
        state: { globalFilter, sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn: "includesString",
    });

    const headerBg = theme.darkMode ? "#374151" : "#f3f4f6";
    const borderColor = theme.darkMode ? "#4b5563" : "#ccc";
    const rowBg = theme.darkMode ? "#1f2937" : "white";
    const rowHoverBg = theme.darkMode ? "#111827" : "#f9fafb";

    const getSortIndicator = (column) => {
        if (!column.getIsSorted()) return "⇅";
        if (column.getIsSorted() === "asc") return "⬆️";
        return "⬇️";
    };

    return (
        <div style={{ padding: 20 }}>
            <h2 style={{ color: theme.text, marginBottom: 20 }}>Appointments</h2>

            {/* Global Search */}
            <input
                type="text"
                placeholder="Search..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                style={{
                    marginBottom: 20,
                    padding: 10,
                    width: "100%",
                    maxWidth: 400,
                    borderRadius: 5,
                    border: `1px solid ${borderColor}`,
                    background: theme.cardBackground,
                    color: theme.text,
                }}
            />

            {appointments.length === 0 ? (
                <p style={{ color: theme.text }}>No appointments found.</p>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table
                        style={{
                            width: "100%",
                            minWidth: 600,
                            borderCollapse: "collapse",
                        }}
                    >
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} style={{ background: headerBg }}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                                padding: 10,
                                                borderBottom: `1px solid ${borderColor}`,
                                                color: theme.text,
                                                textAlign: "left",
                                                cursor: "pointer",
                                                userSelect: "none",
                                            }}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}{" "}
                                            {getSortIndicator(header.column)}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    style={{
                                        background: rowBg,
                                        cursor: "pointer",
                                        color: theme.text, // ← add this
                                    }}
                                    onClick={() =>
                                        window.open(`/appointments/${row.original.id}`, "_blank")
                                    }
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.backgroundColor = rowHoverBg)
                                    }
                                    onMouseLeave={(e) =>
                                        (e.currentTarget.style.backgroundColor = rowBg)
                                    }
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            key={cell.id}
                                            style={{
                                                padding: 10,
                                                borderBottom: `1px solid ${borderColor}`,
                                                color: cell.column.id === "type" || cell.column.id === "service_type" || cell.column.id === "createdAt"
                                                    ? theme.text
                                                    : undefined, // leave status color intact
                                            }}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>

                    </table>
                </div>
            )}
        </div>
    );
}
